import { useNavigate } from 'react-router-dom'

import { Box, SmallIcon, Tag, TagColor, Tooltip } from '@factorialco/deprecated-design-system'
import { TimesheetPageEmployeesSettingFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { AttendanceShiftLocationNodes } from 'modules/attendance/hooks/shift_locations/useFetchShiftLocations'

interface LocationTagProps {
  open?: boolean
  icon: SmallIcon
  textLabel: string
  tagColor: TagColor
  isReadOnly?: boolean
  hasGeofencing: boolean
  locationIssue?: boolean
  outOfRadiusIssue?: boolean
  attendanceSetting?: TimesheetPageEmployeesSettingFragment
  toggle?: (value: boolean) => () => void
  setShowMapPanel?: (value: boolean) => void
  shiftLocations?: AttendanceShiftLocationNodes
}

export const LocationTag = ({
  open,
  icon,
  toggle,
  tagColor,
  textLabel,
  isReadOnly,
  hasGeofencing,
  locationIssue,
  shiftLocations,
  setShowMapPanel,
  outOfRadiusIssue,
  attendanceSetting,
}: LocationTagProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { geolocation, enableClockInLocation } = attendanceSetting ?? {}
  const hasShiftLocations = !!shiftLocations?.length

  const handleShowMapPanel = (withinRadius: boolean) => {
    if (!!setShowMapPanel) {
      setShowMapPanel(true)
      track(trackingEvents.timeTracking.openGeolocationMap, {
        withinRadius,
        hasLocation: enableClockInLocation,
      })
    }
  }

  const handleOutOfRadiusAction = () => {
    if (outOfRadiusIssue) {
      handleShowMapPanel(false)
    } else {
      navigate(navigation.settings.locations.fullPath())
    }
  }

  // show me geolocation on a map
  if (!!geolocation && !hasGeofencing) {
    if (hasShiftLocations) {
      return (
        <Box
          paddingY="s12"
          width="fit-content"
          onClick={isReadOnly ? undefined : () => handleShowMapPanel(false)}
          cursor="pointer"
        >
          <Tag leftItem="icon" icon={icon} type={tagColor} rightItem="forward" showActionIcon>
            {textLabel}
          </Tag>
        </Box>
      )
    }
  }

  // alert me of location away from workplace
  if (hasGeofencing) {
    if (locationIssue || outOfRadiusIssue) {
      const tooltipData = {
        buttonLabel: i18n.t('clock_in.locations.tooltip.view_workplace_button'),
        description: i18n.t('clock_in.locations.tooltip.unknown_radius_description'),
        title: i18n.t('clock_in.locations.tooltip.title_unknown_radius'),
      }

      if (outOfRadiusIssue) {
        tooltipData.buttonLabel = ''
        tooltipData.description = i18n.t('clock_in.locations.tooltip.out_radius_description')
        tooltipData.title = i18n.t('clock_in.locations.tooltip.title_out_radius')
      }

      const onClickHandler = isReadOnly ? undefined : handleOutOfRadiusAction

      return (
        <Tooltip.CallToAction
          attachment="bottom center"
          targetAttachment="top center"
          open={!!open}
          toggle={toggle}
          buttonLabel={tooltipData.buttonLabel}
          description={tooltipData.description}
          title={tooltipData.title}
        >
          <Box paddingY="s12" width="fit-content" cursor="pointer" onClick={onClickHandler}>
            <Tag leftItem="icon" icon={icon} type={tagColor} rightItem="forward" showActionIcon>
              {textLabel}
            </Tag>
          </Box>
        </Tooltip.CallToAction>
      )
    } else if (hasShiftLocations) {
      return (
        <Box
          paddingY="s12"
          width="fit-content"
          onClick={isReadOnly ? undefined : () => handleShowMapPanel(true)}
          cursor="pointer"
        >
          <Tag leftItem="icon" showActionIcon type={tagColor} rightItem="forward" icon={icon}>
            {textLabel}
          </Tag>
        </Box>
      )
    }
  }

  return (
    <Box paddingY="s12" width="fit-content">
      <Tag leftItem="icon" icon={icon} type={tagColor}>
        {textLabel}
      </Tag>
    </Box>
  )
}
